// =====================================================================
// ReconcileMatchDialog (Financeiro Conciliação / S63) — casar uma linha do
// extrato bancário com um lançamento do sistema (mesmo valor, em aberto).
// =====================================================================
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  List, ListItem, ListItemText, CircularProgress, Box
} from "@material-ui/core";
import { toast } from "react-toastify";
import {
  statementCandidates, matchStatementLine, fmtMoney, financeErrorMessage
} from "../../services/financeService";
import useIsMobile from "../../utils/useIsMobile";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "—";
};

const ReconcileMatchDialog = ({ open, line, onClose, onMatched }) => {
  const isMobile = useIsMobile();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && line) {
      setLoading(true);
      statementCandidates(line.id)
        .then((d) => setCandidates(Array.isArray(d?.candidates) ? d.candidates : []))
        .catch(() => setCandidates([]))
        .finally(() => setLoading(false));
    }
  }, [open, line]);

  const handleMatch = async (entryId) => {
    setSaving(true);
    try {
      await matchStatementLine(line.id, entryId);
      toast.success("Lançamento conciliado.");
      onMatched && onMatched();
    } catch (err) {
      toast.error(financeErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Casar com lançamento do sistema</DialogTitle>
      <DialogContent>
        {line && (
          <Box mb={1} p={1} style={{ background: "#f5f5f5", borderRadius: 6 }}>
            <Typography variant="body2">
              <strong>Banco:</strong> {fmtDate(line.postedAt)} · {fmtMoney(line.amount)}
              {line.memo ? ` · ${line.memo}` : ""}
            </Typography>
          </Box>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
        ) : candidates.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            Nenhum lançamento em aberto com esse valor. Se essa linha não corresponde a nada do sistema
            (tarifa bancária, etc.), use "Ignorar" — ou registre um lançamento manual no Extrato e tente de novo.
          </Typography>
        ) : (
          <List dense>
            {candidates.map((c) => (
              <ListItem button key={c.id} disabled={saving} onClick={() => handleMatch(c.id)}>
                <ListItemText
                  primary={`${fmtMoney(c.amount)} — ${c.description || "—"}`}
                  secondary={`${fmtDate(c.entryDate)} · ${c.type === "credito" ? "entrada" : "saída"}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReconcileMatchDialog;
